import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAppSelector } from '@hooks/app/redux';
import useMedicalRecordsList from '@hooks/query/medicalRecords/useMedicalRecordsList';
import { PlusOutlined } from '@icon';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Empty, IconButton, LifeIdDrawer, ListItem, Loader, Modal } from '@ui';
import { useRouteMatch } from 'react-router-dom';
import { IMedicalRecord } from 'types/medicalRecord.types';
import MedicalRecordDelete from '../medical-record-delete';
import MedicalRecordDetail from '../medical-record-detail';
import MedicalRecordForm from '../medical-record-form';
import MedicalRecordItem from './components/medical-record-item';

interface MedicalDataListProps {
  visible: boolean;
  onClose: () => void;
}

const MedicalDataList: React.FC<MedicalDataListProps> = ({ onClose, visible }) => {
  const { formatMessage } = useIntl();
  const { params } = useRouteMatch<{ userId: string }>();
  const providerId = useAppSelector((state) => state.users.currentProvider?.id)!;
  const userId = useAppSelector((state) => state.users.currentUser?.id);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } =
    useMedicalRecordsList(params.userId);

  const parentRef = React.useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? data.length + 1 : data.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 80,
    overscan: 5,
  });

  const { close, data: modalData, modalProps, open } = Modal.useModal<IMedicalRecord | undefined>();
  const {
    modalProps: addProps,
    close: addClose,
    open: addOpen,
  } = Modal.useModal<IMedicalRecord | undefined>();
  const {
    modalProps: removeProps,
    close: removeClose,
    open: removeOpen,
    data: removeData,
  } = Modal.useModal<IMedicalRecord | undefined>();

  const items = rowVirtualizer.getVirtualItems();

  React.useEffect(() => {
    const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

    if (!lastItem) {
      return;
    }

    if (lastItem.index >= data.length - 1 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage, data.length, isFetchingNextPage, items]);

  return (
    <LifeIdDrawer
      title={formatMessage({ id: 'med_records.medical_records' })}
      visible={visible}
      onClose={onClose}
      isInfiniteScroll
      ref={parentRef}
      extra={
        <IconButton
          Icon={PlusOutlined}
          variant="primary"
          size="small"
          shape="round"
          onClick={addOpen}
        />
      }
    >
      <>
        {isLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loader loading variant="on-light" size="medium" />
          </div>
        ) : (
          <>
            {data.length === 0 ? (
              <div className="mdl-no-records">
                <Empty
                  style={{ margin: 32 }}
                  title={<FormattedMessage id="med_records.no_records" />}
                />
              </div>
            ) : (
              <LifeIdDrawer.Scroller
                totalHeight={rowVirtualizer.getTotalSize()}
                startPoint={items[0].start}
              >
                <ListItem.Group>
                  {items.map((virtualRow) => {
                    const isLoaderRow = virtualRow.index > data.length - 1;
                    const item = data[virtualRow.index];
                    const extra = {
                      key: virtualRow.key,
                      'data-index': virtualRow.index,
                      ref: rowVirtualizer.measureElement,
                    };

                    return (
                      <>
                        {isLoaderRow ? (
                          hasNextPage ? (
                            <div className="load-more" {...extra}>
                              <Loader size="small" />
                            </div>
                          ) : (
                            <div className="load-more" {...extra}>
                              Načítať viac
                            </div>
                          )
                        ) : (
                          <MedicalRecordItem
                            item={item}
                            onClick={open}
                            onDelete={removeOpen}
                            providerId={providerId}
                            userId={userId}
                            {...extra}
                          />
                        )}
                      </>
                    );
                  })}
                </ListItem.Group>
              </LifeIdDrawer.Scroller>
            )}
          </>
        )}
      </>
      <MedicalRecordForm
        {...addProps}
        onClose={addClose}
        afterSubmit={refetch}
        clientId={params.userId}
        providerId={providerId}
      />
      <MedicalRecordDetail
        {...modalProps}
        onClose={close}
        recordId={modalData?.id}
        userId={params.userId}
      />
      <MedicalRecordDelete
        {...removeProps}
        item={removeData}
        afterSubmit={refetch}
        onClose={removeClose}
        clientId={params.userId}
        providerId={providerId}
      />
    </LifeIdDrawer>
  );
};

export default React.memo(MedicalDataList);
